<template>
  <div class="flex flex-col gap-4">
    <!--
      The commitment, read back rather than asked again: the step before this
      one collects it, and a split has nothing to divide until it does. Shared
      with the summary's own read-only copy — see InvestorAllocationBar.
    -->
    <InvestorAllocationBar
      :investors
      :commitment
      :currency
    />

    <div class="flex flex-col gap-3">
      <div
        v-for="(investor, index) in investors"
        :key="index"
        class="flex flex-col gap-3 rounded-ds-lg border p-4 shadow-ds-sm"
        :class="isInvestorResolved(investor) ? 'border-border' : 'border-warning'"
      >
        <div class="flex items-center gap-2.5">
          <span
            class="size-2 shrink-0 rounded-full"
            :style="{ background: investorBarColor(index) }"
          />
          <span class="flex-1 text-[10px] font-bold uppercase tracking-ds-caps text-muted-foreground">
            Investor {{ index + 1 }}
          </span>
          <DSButton
            v-if="investors.length > 1"
            type="button"
            variant="ghost"
            size="icon-sm"
            class="shrink-0 text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-subtle-foreground -mt-1"
            aria-label="Remove this investor"
            @click="removeInvestor(index)"
          >
            <Trash2 />
          </DSButton>
        </div>

        <!-- Existing account, or a new one to create — same either/or the ownership step asks. -->
        <div
          v-if="investor.accountMode === WizardAccountMode.EXISTING"
          class="min-w-0"
        >
          <DSFieldLabel
            label="Entity or account"
            class="mb-1.5"
            :mark="investorMark(index)"
          />
          <AccountPicker
            :model-value="investor.accountId"
            :accounts="accountOptions"
            placeholder="Find or create new"
            empty-label="Nothing to pick — create a new account instead."
            @select="account => handlePick(index, account)"
          >
            <template #note>
              Person, trust, company, grouping account and investment account assets only.
              Feed-maintained accounts are excluded.
            </template>
          </AccountPicker>
        </div>
        <div
          v-else
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <div class="min-w-0">
            <DSFieldLabel
              label="Account"
              class="mb-1.5"
              :mark="investorMark(index)"
            />
            <DSInput
              :model-value="investor.accountName"
              placeholder="Ex. John Smith Trust"
              @update:model-value="value => setField(index, 'accountName', String(value ?? ''))"
            />
          </div>
          <div class="min-w-0">
            <DSFieldLabel
              label="Type"
              class="mb-1.5"
              :mark="investorMark(index)"
            />
            <DSSelect
              :model-value="investor.accountType"
              @update:model-value="value => setField(index, 'accountType', String(value))"
            >
              <DSSelectTrigger>
                <DSSelectValue placeholder="Choose one" />
              </DSSelectTrigger>
              <DSSelectContent>
                <DSSelectItem
                  v-for="type in accountTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </DSSelectItem>
              </DSSelectContent>
            </DSSelect>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="min-w-0">
            <DSFieldLabel
              label="Share"
              class="mb-1.5"
              :mark="investorMark(index)"
            />
            <DSAmountField
              :model-value="investor.share"
              placeholder="0.00"
              @update:model-value="value => setShare(index, value)"
            />
          </div>
          <div class="min-w-0">
            <DSFieldLabel
              label="Percent"
              class="mb-1.5"
            />
            <DSInput
              :model-value="percentInput(investor)"
              placeholder="0"
              @update:model-value="value => setPercent(index, String(value ?? ''))"
            />
          </div>
        </div>

        <button
          type="button"
          class="self-start text-xs font-bold text-brand hover:text-brand/80 cursor-pointer"
          @click="toggleMode(index)"
        >
          {{ investor.accountMode === WizardAccountMode.EXISTING
            ? 'Create a new account instead'
            : 'Use an existing account instead' }}
        </button>
      </div>
    </div>

    <div class="flex flex-col sm:flex-row gap-2.5">
      <DSButton
        type="button"
        variant="secondary"
        class="sm:flex-1"
        @click="addInvestor"
      >
        <Plus class="size-4" />
        Add another investor
      </DSButton>
      <DSButton
        type="button"
        variant="secondary"
        @click="splitEvenly"
      >
        <Split class="size-4" />
        Split evenly
      </DSButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Plus, Split, Trash2 } from '@lucide/vue'

import type { FieldLabelMark } from '@ds'
import {
  DSAmountField,
  DSButton,
  DSFieldLabel,
  DSInput,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
} from '@ds'

import AccountPicker from '@/entities/assets/components/AccountPicker.vue'
import { AccountOption } from '@/entities/assets/utils/types'
import { AssetWizardAccount, AssetWizardInvestor } from '@/store/ai/assetWizard/utils/types'

import { numberParse } from '@/helpers/numbers'

import InvestorAllocationBar from './InvestorAllocationBar.vue'

import { isInvestorResolved } from '../utils/schema'
import { emptyInvestor, investorBarColor, investorPercent } from '../utils/helpers'
import { useWizardOptions } from '../utils/options'
import {
  WizardAccountMode,
  WizardCashFlow,
  WizardFieldDef,
  WizardFieldOrigin,
  WizardInvestor,
} from '../utils/types'

/*
 * The one step the generic field grid cannot express: a commitment split
 * across more than one investing entity, each its own asset-and-transaction
 * bundle (see PrivateFundSubscribeAndCommit/create.ts and
 * PrivateSecurity/create.ts) rather than one record with a note. Shared by
 * both flows — private_fund's own entities step is the sibling this used to
 * be the only version of, before that split existed; see EntitiesStep.vue.
 *
 * Share and percent are two views of one value, not two fields: percent is
 * always derived from share against the commitment, and typing into it writes
 * back into share — there is nowhere for the two to disagree.
 *
 * Real account picking only, no "Ask AI" here — the two suggestion assistants
 * both answer about a single account, and this step asks about several at
 * once. A sentence that named a split arrives seeded already — see
 * `seedInvestors` — so the rows below are the read's own answer, not a
 * second ask; a manual entry still opens on one empty row. private_security's
 * own read may suggest a split too, same as private_fund_subscribe_and_commit's,
 * but is never required to — a row it left unsized starts here exactly as a
 * manual entry's would.
 */
const { form, transactionType, investors: suggested } = defineProps<{
  form: any
  fields: WizardFieldDef[]
  origins: Record<string, WizardFieldOrigin>
  /** Which account types may hold this commitment — the flow's own transaction. */
  transactionType: string
  /** Declared so Wizard.vue can hand every bespoke step the same bag. Unused: this step asks about several accounts, not one the flow read. */
  flow?: string
  ownerAccount?: AssetWizardAccount
  /**
   * What the read suggested, if it suggested anything — see `seedInvestors`.
   * The rows themselves are already in the form; this is only for
   * `investorMark`, which needs the count to know how many of them are its.
   */
  investors?: AssetWizardInvestor[]
  sentence?: string
}>()

const { accountsOf, accountTypesFor } = useWizardOptions()

const accountOptions = computed(() => accountsOf(transactionType))
const accountTypes = computed(() => accountTypesFor(transactionType))

/*
 * Read off the form rather than held here: the step is remounted every time
 * the user walks back into it, and investors kept in component state would
 * be lost the moment they left.
 */
const investorsSelector = form.useSelector((state: any): WizardInvestor[] => state.values.investors ?? [])
const commitmentSelector = form.useSelector((state: any) => state.values.amount)
const currencySelector = form.useSelector((state: any) => state.values.currency)

const investors = computed<WizardInvestor[]>(() => investorsSelector.value)
const commitment = computed<number | undefined>(() => commitmentSelector.value)
const currency = computed<string | undefined>(() => currencySelector.value)

/*
 * Sparkle the same rows `seedInvestors` actually filled — the first N,
 * `suggested`'s own count, in the order it seeded them. A row added
 * afterward with "Add another investor" is the user's, not the read's —
 * same rule every other field's sparkle already follows: `ai` wins over
 * `required` regardless of whether the row has since been edited,
 * provenance being about where a row came from, not what it still holds.
 *
 * Every other row gets `required`, same as `originFor` marks the ownership
 * step's own account fields: the account and the share are never optional
 * here, and the mark stays once filled — it says the field is one Allposit
 * would not guess, not that something is still missing (the rail's own
 * outstanding count is what tracks that).
 */
const investorMark = (index: number): FieldLabelMark | undefined =>
  index < (suggested?.length ?? 0) ? 'ai' : 'required'

const setInvestors = (next: WizardInvestor[]) => form.setFieldValue('investors', next)

const updateInvestor = (index: number, patch: Partial<WizardInvestor>) => {
  setInvestors(investors.value.map((investor: WizardInvestor, i: number) =>
    i === index ? { ...investor, ...patch } : investor))
}

/** Picking copies the account's own name and type in beside its id. */
const handlePick = (index: number, account: AccountOption) => {
  updateInvestor(index, { accountId: account.id, accountName: account.name, accountType: account.type })
}

const setField = (index: number, key: 'accountName' | 'accountType', value: string) => {
  updateInvestor(index, { [key]: value })
}

const setShare = (index: number, value: number | undefined) => {
  updateInvestor(index, { share: value })
}

/*
 * Percent only ever writes back into share — it is not a value of its own,
 * so there is nothing here for the two to disagree about.
 */
const setPercent = (index: number, raw: string) => {
  const percent = numberParse(raw)
  if (Number.isNaN(percent) || !commitment.value) return
  updateInvestor(index, { share: commitment.value * (percent / 100) })
}

const percentInput = (investor: WizardInvestor) => investorPercent(investor, commitment.value) ?? ''

/*
 * Switching modes clears the account fields. The alternative — keeping the
 * name of an account the user has just stopped pointing at — leaves a value
 * nobody chose in a field the schema is about to require differently.
 */
const toggleMode = (index: number) => {
  const nextMode = investors.value[index].accountMode === WizardAccountMode.EXISTING
    ? WizardAccountMode.NEW
    : WizardAccountMode.EXISTING
  updateInvestor(index, { accountMode: nextMode, accountId: '', accountName: '', accountType: '' })
}

const addInvestor = () => setInvestors([...investors.value, emptyInvestor()])

/**
 * A cash flow keyed to the row being removed — see WizardCashFlow — would
 * otherwise silently point at whichever investor slides into its old
 * position. Dropped rather than reassigned: which entity a logged call
 * belonged to is not something removing a different row should guess at.
 * Read off the form directly rather than through a selector: this only ever
 * runs once, at the moment of the click, not on every render.
 */
const reindexCashFlows = (removedIndex: number) => {
  const cashFlows: WizardCashFlow[] = form.state.values.cashFlows ?? []
  if (!cashFlows.length) return

  form.setFieldValue(
    'cashFlows',
    cashFlows
      .filter(flow => flow.investorIndex !== removedIndex)
      .map(flow => (flow.investorIndex > removedIndex
        ? { ...flow, investorIndex: flow.investorIndex - 1 }
        : flow)),
  )
}

/** The last investor is not removable — a split needs at least one row. */
const removeInvestor = (index: number) => {
  if (investors.value.length <= 1) return
  setInvestors(investors.value.filter((_: WizardInvestor, i: number) => i !== index))
  reindexCashFlows(index)
}

/** One click, the commitment divided equally across whatever rows exist now. */
const splitEvenly = () => {
  if (!commitment.value || !investors.value.length) return
  const share = commitment.value / investors.value.length
  setInvestors(investors.value.map((investor: WizardInvestor) => ({ ...investor, share })))
}
</script>
